import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CircularProgress, Typography, Tooltip } from '@material-ui/core';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import { pt } from 'date-fns/locale';
import { format } from 'date-fns';

import { ApplicationState } from '../../../store';
import { generateRequest, listRequest, selectInsight } from '../../../store/modules/insights/actions';
import { InsightItem } from '../../../store/modules/insights/types';
import history from '../../../services/history';
import { useStyles } from './InsightsStyles';

export default function InsightsView() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { generating, history: insightHistory, historyLoading, usage } = useSelector(
    (state: ApplicationState) => state.insights
  );

  var [dateFrom, setDateFrom] = useState<Date | null>(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
  var [dateTo, setDateTo] = useState<Date | null>(new Date());

  useEffect(function() {
    dispatch(listRequest());
  }, []); // eslint-disable-line

  function handleGenerate() {
    var from = dateFrom ? format(dateFrom, 'yyyy-MM-dd') : undefined;
    var to = dateTo ? format(dateTo, 'yyyy-MM-dd') : undefined;
    dispatch(generateRequest(from, to));
  }

  function handleSelectInsight(insight: InsightItem) {
    dispatch(selectInsight(insight));
    history.push('/insights/' + insight._id);
  }

  function formatDateStr(dateStr: string) {
    var d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function formatShortDate(dateStr: string) {
    if (!dateStr) return '—';
    var parts = dateStr.split('-');
    return parts[2] + '/' + parts[1] + '/' + parts[0];
  }

  return (
    <div className={classes.pageContainer}>
      <Typography className={classes.pageTitle}>Insights IA</Typography>

      {/* Input Section */}
      <div className={classes.addSection}>
        <div className={classes.addForm}>
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={pt}>
            <KeyboardDatePicker
              disableToolbar
              variant="inline"
              format="dd/MM/yyyy"
              margin="normal"
              label="De"
              value={dateFrom}
              onChange={function(date) { setDateFrom(date); }}
              KeyboardButtonProps={{ 'aria-label': 'data inicio' }}
              autoOk
              style={{ maxWidth: '150px', margin: 0 }}
            />
            <KeyboardDatePicker
              disableToolbar
              variant="inline"
              format="dd/MM/yyyy"
              margin="normal"
              label="Até"
              value={dateTo}
              onChange={function(date) { setDateTo(date); }}
              KeyboardButtonProps={{ 'aria-label': 'data fim' }}
              autoOk
              style={{ maxWidth: '150px', margin: 0 }}
            />
          </MuiPickersUtilsProvider>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              className={classes.ctaButton}
              onClick={handleGenerate}
              disabled={generating || (usage != null && usage.remaining <= 0 && usage.limit > 0)}
            >
              {generating ? 'Gerando...' : 'Gerar Insights'}
            </button>
            {usage && usage.limit > 0 && (
              <Tooltip title={
                (usage.plan === 'trial'
                  ? 'Você tem ' + usage.remaining + ' de ' + usage.limit + ' insights no trial'
                  : 'Você tem ' + usage.remaining + ' de ' + usage.limit + ' gerações disponíveis') +
                (usage.resetsAt ? '. Renova em ' + new Date(usage.resetsAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '') +
                (usage.plan !== 'premium' ? '. Clique para fazer upgrade' : '')
              } arrow placement="top">
                <div
                  className={classes.usageBadge}
                  onClick={usage.plan !== 'premium' ? function() { history.push('/plans'); } : undefined}
                  style={usage.plan !== 'premium' ? { cursor: 'pointer' } : undefined}
                >
                  {usage.plan === 'trial' && (
                    <span style={{ fontSize: '11px', marginRight: '4px', color: '#ffee58' }}>Trial</span>
                  )}
                  <span className={classes.usageCount}>{usage.remaining}</span>
                  <span className={classes.usageSeparator}>/</span>
                  <span className={classes.usageTotal}>{usage.limit}</span>
                </div>
              </Tooltip>
            )}
            {usage && usage.limit === 0 && (
              <Tooltip title="Faça upgrade do seu plano para gerar insights com IA" arrow placement="top">
                <span
                  className={classes.usageBadge}
                  onClick={function() { history.push('/plans'); }}
                  style={{ color: '#FF6347', cursor: 'pointer' }}
                >
                  Upgrade necessário
                </span>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {/* Loading State */}
      {generating && (
        <div className={classes.loadingBox}>
          <CircularProgress style={{ color: '#ffee58' }} size={36} />
          <div className={classes.loadingText}>Analisando seus trades...</div>
        </div>
      )}

      {/* Empty State */}
      {!generating && insightHistory.length === 0 && !historyLoading && (
        <div className={classes.emptyBox}>
          <EmojiObjectsIcon style={{ color: '#ffffff33', fontSize: 40, marginBottom: '12px' }} />
          <div className={classes.emptyState}>
            Selecione o período e clique em "Gerar Insights" para receber análises personalizadas da IA.
          </div>
        </div>
      )}

      {/* History List */}
      {(insightHistory.length > 0 || historyLoading) && (
        <div className={classes.historyBox}>
          <div className={classes.historyBody}>
            {historyLoading && (
              <div style={{ textAlign: 'center', padding: '24px' }}>
                <CircularProgress style={{ color: '#ffee58' }} size={28} />
              </div>
            )}
            {!historyLoading &&
              insightHistory.map(function(item, index) {
                return (
                  <React.Fragment key={item._id}>
                    <div
                      className={classes.historyCard}
                      onClick={function() { handleSelectInsight(item); }}
                    >
                      <div className={classes.historyCardLeft}>
                        <div className={classes.historyCardIcon} style={{
                          color: item.score >= 70 ? '#4BDC4B' : item.score >= 40 ? '#ffee58' : item.score > 0 ? '#FF6347' : '#ffee58',
                          fontSize: '13px',
                          fontWeight: 800,
                        }}>
                          {item.score > 0 ? item.score : <EmojiObjectsIcon style={{ color: '#ffee58', fontSize: 16 }} />}
                        </div>
                        <div className={classes.historyCardInfo}>
                          <div className={classes.historyCardDate}>
                            {formatDateStr(item.generatedAt)}
                          </div>
                          <div className={classes.historyCardStats}>
                            {item.tradeSummary.totalTrades} trades &middot; Win rate <span style={{ color: item.tradeSummary.winRate >= 50 ? '#4BDC4B' : '#FF6347' }}>{item.tradeSummary.winRate}%</span>
                            {item.periodStart && (
                              <span> &middot; {formatShortDate(item.periodStart)} — {formatShortDate(item.periodEnd)}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div
                        className={classes.historyCardPnl}
                        style={{
                          color: item.tradeSummary.totalPnL >= 0 ? '#4BDC4B' : '#FF6347',
                        }}
                      >
                        R$ {item.tradeSummary.totalPnL.toFixed(2)}
                      </div>
                    </div>
                    {index < insightHistory.length - 1 && <div className={classes.historyDivider} />}
                  </React.Fragment>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
